import { alpha, type Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

/**
 * Typography and spacing for rendered Markdown.
 *
 * Kept as a theme-derived `sx` object rather than a stylesheet so every colour
 * resolves from the palette and dark mode needs no parallel rule set.
 */
export const markdownSx = (theme: Theme): SystemStyleObject<Theme> => ({
  color: theme.palette.text.primary,
  fontSize: '1rem',
  lineHeight: 1.72,
  overflowWrap: 'break-word',
  wordBreak: 'break-word',

  '& > :first-of-type': { marginTop: 0 },
  '& > :last-child': { marginBottom: 0 },

  '& h1, & h2, & h3, & h4, & h5, & h6': {
    position: 'relative',
    marginTop: '2.15em',
    marginBottom: '0.65em',
    lineHeight: 1.28,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    scrollMarginTop: 88,
  },
  '& h1': {
    fontFamily: '"Newsreader", Georgia, serif',
    fontSize: 'clamp(2.25rem, 1.55rem + 2.4vw, 3.2rem)',
    fontWeight: 500,
    lineHeight: 1.08,
    letterSpacing: '-0.045em',
    marginTop: 0,
    marginBottom: '0.48em',
  },
  '& h2': {
    fontSize: 'clamp(1.45rem, 1.2rem + 1vw, 1.85rem)',
    marginTop: '2.4em',
  },
  '& h3': { fontSize: '1.3rem' },
  '& h4': { fontSize: '1.1rem' },
  '& h5': { fontSize: '1rem' },
  '& h6': { fontSize: '0.92rem', color: theme.palette.text.secondary },

  '& p': { marginTop: 0, marginBottom: '1.15em' },

  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    textUnderlineOffset: '0.18em',
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.28)}`,
    transition: theme.transitions.create(['color', 'border-color', 'background-color'], {
      duration: 140,
    }),
    '&:hover': {
      color: theme.palette.primary.dark,
      borderBottomColor: theme.palette.primary.main,
    },
  },

  '& strong': { fontWeight: 700, color: theme.palette.text.primary },
  '& em': { fontStyle: 'italic' },
  '& del, & s': { color: theme.palette.text.secondary, textDecorationThickness: '1.5px' },
  '& mark': {
    backgroundColor: theme.markdown.searchHit,
    color: 'inherit',
    borderRadius: 3,
    padding: '0.05em 0.15em',
  },

  '& ul, & ol': { marginTop: 0, marginBottom: '1.15em', paddingLeft: '1.6em' },
  '& li': { marginBottom: '0.35em' },
  '& li > p': { marginBottom: '0.6em' },
  '& li::marker': { color: theme.palette.text.secondary },
  '& ul ul, & ul ol, & ol ul, & ol ol': { marginTop: '0.35em', marginBottom: '0.35em' },

  '& ul.contains-task-list, & .contains-task-list': { listStyle: 'none', paddingLeft: '0.25em' },
  '& li.task-list-item': { display: 'flex', alignItems: 'flex-start', gap: '0.15em' },
  '& li.task-list-item > p': { margin: 0 },

  '& hr': {
    height: 1,
    border: 0,
    margin: '2.5em 0',
    background: theme.palette.divider,
  },

  '& blockquote': {
    margin: '0 0 1.3em',
    padding: '0.4em 0 0.4em 1.2em',
    borderLeft: `3px solid ${theme.markdown.quoteBar}`,
    color: theme.palette.text.secondary,
    fontStyle: 'normal',
    '& > :last-child': { marginBottom: 0 },
  },

  '& kbd': {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.8em',
    padding: '0.15em 0.45em',
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    borderBottomWidth: 2,
    background: theme.markdown.surfaceSunken,
    color: theme.palette.text.primary,
  },

  '& abbr[title]': { textDecoration: `underline dotted ${theme.palette.text.secondary}`, cursor: 'help' },

  '& dl.definition-list': {
    margin: '0 0 1.3em',
    padding: '0.9em 1.1em',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    background: theme.markdown.surfaceSunken,
  },
  '& dl.definition-list dt': {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginTop: '0.7em',
    '&:first-of-type': { marginTop: 0 },
  },
  '& dl.definition-list dd': {
    margin: '0.2em 0 0 0',
    paddingLeft: '1em',
    borderLeft: `2px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },

  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
  },

  '& sup': { lineHeight: 0 },
  '& sub': { lineHeight: 0 },

  // GFM footnote section.
  '& section.footnotes': {
    marginTop: '3em',
    paddingTop: '1.2em',
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: '0.92rem',
    color: theme.palette.text.secondary,
    '& h2': { fontSize: '1rem', border: 0, margin: '0 0 0.6em', padding: 0 },
    '& li:target': {
      background: alpha(theme.palette.primary.main, 0.1),
      borderRadius: 6,
    },
  },
  '& [data-footnote-ref]': {
    borderBottom: 'none',
    padding: '0 0.15em',
    fontWeight: 600,
  },

  // KaTeX: keep long display equations scrollable instead of overflowing.
  '& .katex-display': {
    margin: '1.4em 0',
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: '0.4em',
  },
  '& .katex': { fontSize: '1.05em' },
  '& .katex-error': {
    color: theme.palette.error.main,
    background: alpha(theme.palette.error.main, 0.08),
    borderRadius: 4,
    padding: '0.1em 0.3em',
    fontFamily: 'monospace',
    fontSize: '0.85em',
  },

  '& .search-hit': {
    backgroundColor: theme.markdown.searchHit,
    borderRadius: 3,
    padding: '0.05em 0.1em',
    scrollMarginTop: 120,
  },
  '& .search-hit[data-active="true"]': {
    backgroundColor: theme.markdown.searchHitActive,
    color: theme.palette.getContrastText(theme.markdown.searchHitActive),
    boxShadow: `0 0 0 2px ${theme.markdown.searchHitActive}`,
  },

  '& .markdown-parse-fallback': {
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: '0.85rem',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '0.96rem',
    '& h1': { marginBottom: '0.5em' },
    '& ul, & ol': { paddingLeft: '1.25em' },
  },
});
